import { useState, useMemo } from "react";
import FilterPanel from "../filters/FilterPanel";
import IndustryAnalysisChart from "../charts/analysis/IndustryAnalysisChart";
import { TimelineChart } from "../charts/timeline/TimelineChart";
import ChartErrorBoundary from "../charts/shared/error/ChartErrorBoundary";
import PhaseAnalysisChart from "../charts/analysis/PhaseAnalysisChart";
import CantonAnalysisChart from "../charts/analysis/CantonAnalysisChart";
import GenderAnalysisChart from "../charts/analysis/GenderAnalysisChart";
import NoDataMessage from "../charts/shared/layout/NoDataMessage";
import styles from "./Dashboard.module.css";
import layoutStyles from "../../styles/utilities/layout.module.css";
import YearRangeSelector from "./YearRangeSelector";
import MobileTabs from "./MobileTabs";
import AnalysisChartWrapper from "./AnalysisChartWrapper";
import {
  useDashboardData,
  useDashboardFilters,
  useChartData,
  useDropdown,
  useMobileChartTabs,
} from "../../hooks";

type ChartKey = "timeline" | "industry" | "phase" | "canton" | "ceoGender";
type FilterKey = "yearRange" | "industries" | "phases" | "cantons" | "ceoGenders";
type ArrayFilterKey = "industries" | "phases" | "cantons" | "ceoGenders";
type ChartTab = { key: ChartKey; label: string };

const Dashboard = () => {
  const { deals, loading, filterOptions, initialYearRange } = useDashboardData();

  const [activeChart, setActiveChart] = useState<ChartKey>("timeline");

  const { tabs: mobileTabs, setTab: setMobileTab } = useMobileChartTabs();

  const chartTabs: ChartTab[] = useMemo(
    () => [
      { key: "timeline", label: "Total Investments/Deals" },
      { key: "industry", label: "Industries" },
      { key: "phase", label: "Stages" },
      { key: "canton", label: "Canton" },
      { key: "ceoGender", label: "Gender" },
    ],
    []
  );

  const { globalFilters, chartFilters, updateFilter, toggleArrayFilter, resetFilters } =
    useDashboardFilters(initialYearRange);

  const {
    baseFilteredDeals,
    industryDeals,
    phaseDeals,
    cantonDeals,
    genderDeals,
    chartData,
    previewChartData,
    timelineSharedYTickCount,
    analysisSharedYTickCount,
    yTickValuesVolume,
    yTickValuesCount,
    hasTimelineData,
    hasIndustryData,
    hasPhaseData,
    hasCantonData,
    hasGenderData,
    previewHideFinalYear,
  } = useChartData(deals, globalFilters, chartFilters, filterOptions.currentHalf);

  const {
    isOpen: isDropdownOpen,
    setIsOpen: setIsDropdownOpen,
    selectedKey: mobileActiveChart,
    dropdownRef,
    handleSelect: handleMobileChartSelect,
  } = useDropdown<ChartKey>(chartTabs, "timeline");

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.textCenter}>
          <div className={styles.spinner} />
          <p className={styles.textMuted}>Loading Swiss startup ecosystem data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${layoutStyles.grid} ${layoutStyles.gridCols1} ${styles.dashboardContainer}`}>
      <div className="inner-panel">
        <section className={`${styles.tabPanel} ${styles.innerContainer}`}>
          <div className={styles.tabBarWrap}>
            <div className={styles.tabBar}>
              {chartTabs.map((tab) => {
                const isActive = activeChart === tab.key;
                const btnClass = `${styles.tabButton} ${isActive ? styles.active : styles.inactive}`;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveChart(tab.key)}
                    className={btnClass}
                    title={tab.label}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={styles.mobileChartSelector}>
            <div className={styles.customSelectWrapper} ref={dropdownRef as any}>
              <button
                className={styles.customSelectTrigger}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
                aria-label="Select chart view"
                type="button"
              >
                <span className={styles.customSelectValue}>
                  {chartTabs.find((tab) => tab.key === mobileActiveChart)?.label}
                </span>
                <svg
                  className={`${styles.customSelectArrow} ${isDropdownOpen ? styles.customSelectArrowOpen : ""}`}
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="#4A4A4A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isDropdownOpen && (
                <ul className={styles.customSelectDropdown} role="listbox">
                  {chartTabs.map((tab) => (
                    <li
                      key={tab.key}
                      className={`${styles.customSelectOption} ${
                        mobileActiveChart === tab.key ? styles.customSelectOptionActive : ""
                      }`}
                      onClick={() => handleMobileChartSelect(tab.key)}
                      role="option"
                      aria-selected={mobileActiveChart === tab.key}
                      aria-label={tab.label}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleMobileChartSelect(tab.key);
                        }
                      }}
                    >
                      {tab.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className={styles.mobileChartsArea}>
            {mobileActiveChart === "timeline" && (
              <>
                {hasTimelineData ? (
                  <div className={styles.mobileChartWrapper}>
                    <MobileTabs
                      value={mobileTabs.timeline}
                      onChange={(v) => setMobileTab("timeline", v)}
                    />

                    {mobileTabs.timeline === "volume" && (
                      <ChartErrorBoundary chartName="Timeline Volume">
                        <TimelineChart
                          data={chartData.timeline}
                          previewData={previewChartData.timeline}
                          previewHideFinalYear={previewHideFinalYear}
                          showVolume={true}
                          title="Invested Capital"
                          yTickCount={timelineSharedYTickCount}
                          yTickValues={yTickValuesVolume}
                          mobileShowOnly={mobileTabs.timeline}
                        />
                      </ChartErrorBoundary>
                    )}

                    {mobileTabs.timeline === "count" && (
                      <ChartErrorBoundary chartName="Timeline Count">
                        <TimelineChart
                          data={chartData.timeline}
                          previewData={previewChartData.timeline}
                          previewHideFinalYear={previewHideFinalYear}
                          showVolume={false}
                          title="Number of deals"
                          yTickCount={timelineSharedYTickCount}
                          yTickValues={yTickValuesCount}
                          mobileShowOnly={mobileTabs.timeline}
                        />
                      </ChartErrorBoundary>
                    )}
                  </div>
                ) : (
                  <div className={styles.mobileChartWrapper}>
                    <NoDataMessage
                      message="No deals found for this time period"
                      suggestion="Try selecting a different year range above"
                    />
                  </div>
                )}
                <YearRangeSelector
                  filterOptions={filterOptions}
                  globalFilters={globalFilters}
                  updateFilter={(key, value, target) =>
                    updateFilter(key as FilterKey, value, target)
                  }
                  targetChart={mobileActiveChart}
                />

                <div className={styles.timelineMessagePlain}>
                  <h3 className={styles.overviewMessageTitle}>How to use the dashboard</h3>
                  <p className={styles.overviewMessageText}>
                    Check out the category tabs Industries, Stages, Cantons and CEO gender above the
                    graphs to filter the data from our database.
                  </p>
                  <p className={`${styles.overviewMessageText} ${styles.desktopOnly}`}>
                    HY data is visible in expanded view only.
                  </p>
                  <p className={`${styles.overviewMessageText} ${styles.mobileOnly}`}>
                    Some features like "detail view" and "download" are in desktop view available
                    only.
                  </p>
                  <p className={`${styles.overviewMessageText} ${styles.mobileOnly}`}>
                    The dashboard shows the current status of the data, which in rare cases is
                    corrected after publication in the Venture Capital Report. The data on the
                    dashboard therefore differs slightly from the published data.
                  </p>
                  <p className={`${styles.overviewMessageText} ${styles.mobileOnly}`}>
                    Data in the dashboard will be updated twice a year.
                  </p>
                </div>
              </>
            )}

            {mobileActiveChart === "industry" && (
              <AnalysisChartWrapper
                hasData={hasIndustryData}
                chartName="Industry Analysis"
                noDataMessage="No industry data matches your selection"
                noDataSuggestion="Try selecting different industries or clearing the filters below"
              >
                <MobileTabs
                  value={mobileTabs.industry}
                  onChange={(v) => setMobileTab("industry", v)}
                />
                <IndustryAnalysisChart
                  deals={industryDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.industry.industries}
                  yTickCount={analysisSharedYTickCount}
                  mobileShowOnly={mobileTabs.industry}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            {mobileActiveChart === "phase" && (
              <AnalysisChartWrapper
                hasData={hasPhaseData}
                chartName="Phase Analysis"
                noDataMessage="No stage data matches your selection"
                noDataSuggestion="Try selecting different stages or clearing the filters below"
              >
                <MobileTabs value={mobileTabs.phase} onChange={(v) => setMobileTab("phase", v)} />
                <PhaseAnalysisChart
                  deals={phaseDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.phase.phases}
                  yTickCount={analysisSharedYTickCount}
                  mobileShowOnly={mobileTabs.phase}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            {mobileActiveChart === "canton" && (
              <AnalysisChartWrapper
                hasData={hasCantonData}
                chartName="Canton Analysis"
                noDataMessage="No canton data matches your selection"
                noDataSuggestion="Try selecting different cantons or clearing the filters below"
              >
                <MobileTabs value={mobileTabs.canton} onChange={(v) => setMobileTab("canton", v)} />
                <CantonAnalysisChart
                  deals={cantonDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.canton.cantons}
                  yTickCount={analysisSharedYTickCount}
                  mobileShowOnly={mobileTabs.canton}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            {mobileActiveChart === "ceoGender" && (
              <AnalysisChartWrapper
                hasData={hasGenderData}
                chartName="Gender Analysis"
                noDataMessage="No CEO gender data matches your selection"
                noDataSuggestion="Try selecting different options or clearing the filters below"
              >
                <MobileTabs value={mobileTabs.gender} onChange={(v) => setMobileTab("gender", v)} />
                <GenderAnalysisChart
                  deals={genderDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.ceoGender.ceoGenders}
                  yTickCount={analysisSharedYTickCount}
                  mobileShowOnly={mobileTabs.gender}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            <div className={styles.filtersWrap}>
              <FilterPanel
                filters={{
                  yearRange: globalFilters.yearRange,
                  industries: chartFilters[mobileActiveChart].industries,
                  phases: chartFilters[mobileActiveChart].phases,
                  ceoGenders: chartFilters[mobileActiveChart].ceoGenders,
                  cantons: chartFilters[mobileActiveChart].cantons,
                }}
                filterOptions={filterOptions}
                activeTab="deals"
                activeChart={mobileActiveChart}
                updateFilter={(key, value) =>
                  updateFilter(key as FilterKey, value, mobileActiveChart, activeChart)
                }
                toggleArrayFilter={(key, value) =>
                  toggleArrayFilter(key as ArrayFilterKey, value, mobileActiveChart, activeChart)
                }
                resetFilters={() => resetFilters(mobileActiveChart, activeChart)}
                isMobile={true}
              />
            </div>
          </div>

          <div className={styles.chartsArea}>
            {activeChart === "timeline" && (
              <div
                className={`${layoutStyles.grid} ${layoutStyles.gridCols1} ${layoutStyles.mdGridCols2} ${layoutStyles.gridGap15} ${styles.timelineGrid || ""}`}
              >
                {hasTimelineData ? (
                  <>
                    <ChartErrorBoundary chartName="Timeline Volume">
                      <TimelineChart
                        data={chartData.timeline}
                        previewData={previewChartData.timeline}
                        previewHideFinalYear={previewHideFinalYear}
                        showVolume={true}
                        title="Invested Capital"
                        yTickCount={timelineSharedYTickCount}
                        yTickValues={yTickValuesVolume}
                        mobileShowOnly={undefined}
                      />
                    </ChartErrorBoundary>
                    <ChartErrorBoundary chartName="Timeline Count">
                      <TimelineChart
                        data={chartData.timeline}
                        previewData={previewChartData.timeline}
                        previewHideFinalYear={previewHideFinalYear}
                        showVolume={false}
                        title="Number of deals"
                        yTickCount={timelineSharedYTickCount}
                        yTickValues={yTickValuesCount}
                        mobileShowOnly={undefined}
                      />
                    </ChartErrorBoundary>
                  </>
                ) : (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <NoDataMessage
                      message="No deals found for this time period"
                      suggestion="Try selecting a different year range above"
                    />
                  </div>
                )}
                <YearRangeSelector
                  filterOptions={filterOptions}
                  globalFilters={globalFilters}
                  updateFilter={(key, value) => updateFilter(key as FilterKey, value)}
                />

                <div className={styles.timelineMessagePlain}>
                  <h3 className={styles.overviewMessageTitle}>How to use the dashboard</h3>
                  <p className={styles.overviewMessageText}>
                    Check out the category tabs Industries, Stages, Cantons and CEO gender above the
                    graphs to filter the data from our database.
                  </p>
                  <p className={styles.overviewMessageText}>
                    HY data is visible in expanded view only.
                  </p>
                  <p className={styles.overviewMessageText}>
                    The dashboard shows the current status of the data, which in rare cases is
                    corrected after publication in the Venture Capital Report. The data on the
                    dashboard therefore differs slightly from the published data.
                  </p>
                  <p className={styles.overviewMessageText}>
                    Data in the dashboard will be updated twice a year.
                  </p>
                </div>
              </div>
            )}

            {activeChart === "industry" && (
              <AnalysisChartWrapper
                hasData={hasIndustryData}
                chartName="Industry Analysis"
                noDataMessage="No industry data matches your selection"
                noDataSuggestion="Try selecting different industries or clearing the filters below"
              >
                <IndustryAnalysisChart
                  deals={industryDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.industry.industries}
                  yTickCount={analysisSharedYTickCount}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            {activeChart === "phase" && (
              <AnalysisChartWrapper
                hasData={hasPhaseData}
                chartName="Phase Analysis"
                noDataMessage="No stage data matches your selection"
                noDataSuggestion="Try selecting different stages or clearing the filters below"
              >
                <PhaseAnalysisChart
                  deals={phaseDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.phase.phases}
                  yTickCount={analysisSharedYTickCount}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            {activeChart === "canton" && (
              <AnalysisChartWrapper
                hasData={hasCantonData}
                chartName="Canton Analysis"
                noDataMessage="No canton data matches your selection"
                noDataSuggestion="Try selecting different cantons or clearing the filters below"
              >
                <CantonAnalysisChart
                  deals={cantonDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.canton.cantons}
                  yTickCount={analysisSharedYTickCount}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            {activeChart === "ceoGender" && (
              <AnalysisChartWrapper
                hasData={hasGenderData}
                chartName="Gender Analysis"
                noDataMessage="No CEO gender data matches your selection"
                noDataSuggestion="Try selecting different options or clearing the filters below"
              >
                <GenderAnalysisChart
                  deals={genderDeals}
                  allDeals={baseFilteredDeals}
                  selectedCategories={chartFilters.ceoGender.ceoGenders}
                  yTickCount={analysisSharedYTickCount}
                  currentHalf={filterOptions.currentHalf}
                />
              </AnalysisChartWrapper>
            )}

            <div className={styles.filtersWrap}>
              <FilterPanel
                filters={{
                  yearRange: globalFilters.yearRange,
                  industries: chartFilters[activeChart].industries,
                  phases: chartFilters[activeChart].phases,
                  ceoGenders: chartFilters[activeChart].ceoGenders,
                  cantons: chartFilters[activeChart].cantons,
                }}
                filterOptions={filterOptions}
                activeTab="deals"
                activeChart={activeChart}
                updateFilter={(key, value) =>
                  updateFilter(key as FilterKey, value, null as any, activeChart)
                }
                toggleArrayFilter={(key, value) =>
                  toggleArrayFilter(key as ArrayFilterKey, value, null as any, activeChart)
                }
                resetFilters={() => resetFilters(null as any, activeChart)}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
